import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { User, Mail, Lock, AtSign } from 'lucide-react'
import { useAuthStore } from '@/store/authStore'

const PRIVACY_OPTIONS = [
  { value: 'private', label: '🔒 Private', desc: 'Only you can see your entries' },
  { value: 'friends', label: '👥 Friends', desc: 'Visible to people you approve' },
  { value: 'public',  label: '🌎 Public',  desc: 'Anyone on Timeline can view' },
] as const

const STEPS = ['Account', 'Privacy', 'Done']

export default function SignUpPage() {
  const [step, setStep]         = useState(0)
  const [fullName, setFullName] = useState('')
  const [username, setUsername] = useState('')
  const [email, setEmail]       = useState('')
  const [password, setPassword] = useState('')
  const [privacy, setPrivacy]   = useState<'private' | 'friends' | 'public'>('private')
  const [agreed, setAgreed]     = useState(false)
  const [error, setError]       = useState('')
  const { signUp, updateProfile, loading } = useAuthStore()
  const navigate = useNavigate()

  const handleCreate = async () => {
    setError('')
    try {
      await signUp(email, password, fullName, username)
      await updateProfile({ default_privacy: privacy })
      navigate('/timeline')
    } catch (err: any) {
      setError(err.message ?? 'Sign up failed')
      setStep(0)
    }
  }

  return (
    <div className="min-h-dvh bg-bg flex flex-col px-6 pt-16 pb-8 safe-top">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col gap-6 max-w-sm mx-auto w-full"
      >
        {/* Logo + title */}
        <div className="flex flex-col items-center gap-3">
          <img src="/icon.png" alt="Timeline Journal" className="w-16 h-16 rounded-2xl" />
          <h1 className="font-serif text-3xl font-bold text-white">Begin your Timeline</h1>
        </div>

        {/* Progress bar */}
        <div className="flex gap-2">
          {STEPS.map((s, i) => (
            <div key={s} className="flex-1 flex flex-col items-center gap-1">
              <div className={`h-1.5 rounded-full w-full transition-all duration-300 ${i <= step ? 'bg-blue' : 'bg-white/10'}`} />
              <span className={`text-[9px] font-mono tracking-wider ${i === step ? 'text-blue-light' : 'text-white/20'}`}>
                {s}
              </span>
            </div>
          ))}
        </div>

        {/* Step 0 — Account info */}
        {step === 0 && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex flex-col gap-4"
          >
            <div className="relative">
              <User size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/30" />
              <input type="text" placeholder="Full name" value={fullName}
                onChange={e => setFullName(e.target.value)} className="input-field pl-10" required />
            </div>
            <div className="relative">
              <AtSign size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/30" />
              <input type="text" placeholder="Username" value={username}
                onChange={e => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                className="input-field pl-10" required />
            </div>
            <div className="relative">
              <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/30" />
              <input type="email" placeholder="Email" value={email}
                onChange={e => setEmail(e.target.value)} className="input-field pl-10" required />
            </div>
            <div className="relative">
              <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/30" />
              <input type="password" placeholder="Password (min 8 characters)" value={password}
                onChange={e => setPassword(e.target.value)} className="input-field pl-10"
                required minLength={8} />
            </div>

            {/* Terms + Privacy agreement */}
            <label className="flex items-start gap-3 cursor-pointer">
              <div
                onClick={() => setAgreed(!agreed)}
                className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-all
                  ${agreed ? 'bg-blue border-blue' : 'border-white/20'}`}
              >
                {agreed && <span className="text-white text-xs font-bold">✓</span>}
              </div>
              <p className="text-white/40 text-sm leading-relaxed">
                I agree to the{' '}
                <Link to="/privacy" className="text-blue-light underline" target="_blank">
                  Privacy Policy
                </Link>
                {' '}and{' '}
                <Link to="/terms" className="text-blue-light underline" target="_blank">
                  Terms of Service
                </Link>
              </p>
            </label>

            <button
              onClick={() => setStep(1)}
              disabled={!fullName || !username || !email || password.length < 8 || !agreed}
              className="btn-primary mt-2"
            >
              Continue →
            </button>
          </motion.div>
        )}

        {/* Step 1 — Privacy default */}
        {step === 1 && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex flex-col gap-4"
          >
            <p className="text-white/50 text-sm text-center leading-relaxed">
              Choose your default privacy. You can change this per entry, anytime.
            </p>
            <div className="flex flex-col gap-3">
              {PRIVACY_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setPrivacy(opt.value)}
                  className={`flex items-center gap-4 p-4 rounded-2xl border text-left transition-all
                    ${privacy === opt.value ? 'border-blue bg-blue/10' : 'border-white/[0.07] bg-white/5'}`}
                >
                  <div className="flex-1">
                    <div className="font-semibold text-white text-sm">{opt.label}</div>
                    <div className="text-white/40 text-xs mt-0.5">{opt.desc}</div>
                  </div>
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center
                    ${privacy === opt.value ? 'border-blue bg-blue' : 'border-white/20'}`}>
                    {privacy === opt.value && <div className="w-2 h-2 rounded-full bg-white" />}
                  </div>
                </button>
              ))}
            </div>
            <button onClick={handleCreate} disabled={loading} className="btn-primary mt-2">
              {loading ? 'Creating your timeline…' : 'Create Timeline'}
            </button>
          </motion.div>
        )}

        {error && (
          <p className="text-rose text-sm text-center bg-rose/10 border border-rose/20 rounded-xl px-4 py-2">
            {error}
          </p>
        )}

        <p className="text-center text-white/30 text-sm">
          Already have a Timeline?{' '}
          <Link to="/login" className="text-blue-light underline">Sign in</Link>
        </p>
      </motion.div>
    </div>
  )
}
